import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GameState, Player } from "../types/game";

interface EndGameScreenProps {
  gameState: GameState;
  winnerId: string;
  winnerName?: string;
  currentUserId: string;
  onPlayAgain?: () => void;
  onBackToLobby: () => void;
}

interface ConfettiParticle {
  id: number;
  x: number;
  y: number;
  color: string;
  rotation: number;
  size: number;
  velocityX: number;
  velocityY: number;
  rotationSpeed: number;
}

// UNO colors for confetti
const UNO_COLORS = ["#E53935", "#1E88E5", "#43A047", "#FDD835"]; // Red, Blue, Green, Yellow

// Generate confetti particles
const generateConfetti = (count: number = 50): ConfettiParticle[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -10 - Math.random() * 20,
    color: UNO_COLORS[Math.floor(Math.random() * UNO_COLORS.length)],
    rotation: Math.random() * 360,
    size: 8 + Math.random() * 12,
    velocityX: (Math.random() - 0.5) * 2,
    velocityY: 2 + Math.random() * 3,
    rotationSpeed: (Math.random() - 0.5) * 10,
  }));
};

export const EndGameScreen: React.FC<EndGameScreenProps> = ({
  gameState,
  winnerId,
  winnerName,
  currentUserId,
  onPlayAgain,
  onBackToLobby,
}) => {
  const [confetti, setConfetti] = useState<ConfettiParticle[]>([]);
  const [showStats, setShowStats] = useState(false);
  const [canSkip, setCanSkip] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const isWinner = winnerId === currentUserId;
  const winner = gameState.players.find((p) => p.id === winnerId);
  const displayName = winnerName || winner?.name || "Player";

  // Calculate game stats
  const stats = useMemo(() => {
    const myPlayer = gameState.players.find((p) => p.id === currentUserId);
    const myCardsRemaining = myPlayer?.cardsCount || 0;
    const totalCardsPlayed = gameState.discardPile.length;
    
    // Count penalty cards (Draw 2 and Wild Draw 4)
    const penaltyCardsUsed = gameState.discardPile.filter(
      (card) => card.value === "draw2" || card.value === "wild4"
    ).length;

    return {
      cardsRemaining: myCardsRemaining,
      totalCardsPlayed,
      penaltyCardsUsed,
    };
  }, [gameState, currentUserId]);

  // Initialize confetti on mount
  useEffect(() => {
    if (isWinner) {
      // Generate confetti with slight delay for better visual effect
      const confettiTimer = setTimeout(() => {
        setConfetti(generateConfetti(60));
      }, 100);
      
      return () => clearTimeout(confettiTimer);
    }
  }, [isWinner]);

  // Handle skip and stats timing
  useEffect(() => {
    // Allow skipping after 1 second
    const skipTimer = setTimeout(() => {
      setCanSkip(true);
    }, 1000);

    // Show stats after 1.5 seconds
    const statsTimer = setTimeout(() => {
      setShowStats(true);
    }, 1500);

    return () => {
      clearTimeout(skipTimer);
      clearTimeout(statsTimer);
    };
  }, []);

  // Animate confetti
  useEffect(() => {
    if (confetti.length === 0) return;

    let animationFrameId: number;
    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTime) / 16; // Normalize to 60fps
      lastTime = currentTime;

      setConfetti((prev) => {
        const updated = prev.map((particle) => ({
          ...particle,
          y: particle.y + particle.velocityY * deltaTime,
          x: particle.x + particle.velocityX * deltaTime,
          rotation: particle.rotation + particle.rotationSpeed * deltaTime,
          velocityY: particle.velocityY + 0.1 * deltaTime, // Gravity
        })).filter((particle) => particle.y < 110); // Remove particles that fall off screen

        // Stop animation if all particles are gone
        if (updated.length === 0) {
          return [];
        }
        return updated;
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [confetti.length]);

  const handleSkip = () => {
    if (!canSkip || isSkipping) return;
    setIsSkipping(true);
    setShowStats(true);
  };

  const handlePlayAgain = () => {
    if (onPlayAgain) {
      onPlayAgain();
    }
  };

  const handleBackToLobby = () => {
    onBackToLobby();
  };

  // Headlines for winner/loser
  const winnerHeadlines = ["YOU WIN!", "VICTORY!", "WINNER!", "CHAMPION!"];
  const loserHeadlines = ["Good try!", "So close!", "Nice game!", "Well played!"];
  const headline = isWinner
    ? winnerHeadlines[Math.floor(Math.random() * winnerHeadlines.length)]
    : loserHeadlines[Math.floor(Math.random() * loserHeadlines.length)];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={styles.overlay}
      onClick={canSkip && !showStats ? handleSkip : undefined}
    >
      {/* Background blur */}
      <motion.div
        initial={{ backdropFilter: "blur(0px)" }}
        animate={{ backdropFilter: "blur(8px)" }}
        transition={{ duration: 0.4 }}
        style={styles.backdrop}
      />

      {/* Confetti */}
      {isWinner && (
        <div style={styles.confettiContainer}>
          {confetti.map((particle) => (
            <motion.div
              key={particle.id}
              style={{
                ...styles.confettiParticle,
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                backgroundColor: particle.color,
                width: particle.size,
                height: particle.size,
                transform: `rotate(${particle.rotation}deg)`,
              }}
            />
          ))}
        </div>
      )}

      {/* Main content */}
      <div style={styles.content}>
        {/* Winner Avatar & Crown */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.1,
          }}
          style={styles.avatarContainer}
        >
          <motion.div
            animate={{
              scale: isWinner ? [1, 1.1, 1] : 1,
              boxShadow: isWinner
                ? [
                    "0 0 0px rgba(255, 215, 0, 0)",
                    "0 0 40px rgba(255, 215, 0, 0.8)",
                    "0 0 0px rgba(255, 215, 0, 0)",
                  ]
                : "0 0 0px rgba(255, 255, 255, 0)",
            }}
            transition={{
              duration: 2,
              repeat: isWinner ? Infinity : 0,
              ease: "easeInOut",
            }}
            style={{
              ...styles.avatar,
              background: isWinner
                ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            {displayName.charAt(0).toUpperCase()}
          </motion.div>

          {/* Crown for winner */}
          {isWinner && (
            <motion.div
              initial={{ y: -20, opacity: 0, rotate: -10 }}
              animate={{ y: 0, opacity: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 15,
                delay: 0.3,
              }}
              style={styles.crown}
            >
              👑
            </motion.div>
          )}
        </motion.div>

        {/* Winner Name */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          style={styles.winnerName}
        >
          {displayName}
        </motion.h1>

        {/* Headline */}
        <motion.h2
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 200,
            delay: 0.3,
          }}
          style={{
            ...styles.headline,
            color: isWinner ? "#FFD700" : "rgba(255, 255, 255, 0.9)",
            fontSize: isWinner ? "clamp(32px, 8vw, 56px)" : "clamp(24px, 6vw, 40px)",
          }}
        >
          {headline}
        </motion.h2>

        {/* Stats Section */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              style={styles.statsContainer}
            >
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{stats.cardsRemaining}</div>
                  <div style={styles.statLabel}>Cards Left</div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{stats.totalCardsPlayed}</div>
                  <div style={styles.statLabel}>Cards Played</div>
                </div>
                {stats.penaltyCardsUsed > 0 && (
                  <div style={styles.statItem}>
                    <div style={styles.statValue}>{stats.penaltyCardsUsed}</div>
                    <div style={styles.statLabel}>Penalty Cards</div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              style={styles.actionsContainer}
            >
              {onPlayAgain && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePlayAgain}
                  style={styles.primaryButton}
                >
                  Play Again
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBackToLobby}
                style={styles.secondaryButton}
              >
                Back to Lobby
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip hint (only show before stats appear) */}
        {canSkip && !showStats && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            style={styles.skipHint}
          >
            Tap anywhere to continue
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    overflow: "hidden",
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    pointerEvents: "none",
  },
  confettiContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    overflow: "hidden",
  },
  confettiParticle: {
    position: "absolute",
    borderRadius: "2px",
    pointerEvents: "none",
    willChange: "transform",
  },
  content: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    maxWidth: "90%",
    width: "100%",
  },
  avatarContainer: {
    position: "relative",
    marginBottom: "20px",
  },
  avatar: {
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "48px",
    fontWeight: "900",
    color: "white",
    textShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
    border: "4px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  },
  crown: {
    position: "absolute",
    top: "-20px",
    left: "50%",
    transform: "translateX(-50%)",
    fontSize: "48px",
    filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))",
  },
  winnerName: {
    fontSize: "clamp(28px, 7vw, 48px)",
    fontWeight: "900",
    color: "white",
    margin: "0 0 16px 0",
    textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
    letterSpacing: "1px",
  },
  headline: {
    fontSize: "clamp(32px, 8vw, 56px)",
    fontWeight: "900",
    margin: "0 0 32px 0",
    textShadow: "0 4px 20px rgba(0, 0, 0, 0.6)",
    letterSpacing: "2px",
  },
  statsContainer: {
    width: "100%",
    maxWidth: "400px",
    marginBottom: "32px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
    gap: "16px",
    padding: "20px",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  },
  statValue: {
    fontSize: "clamp(32px, 6vw, 48px)",
    fontWeight: "900",
    color: "white",
    textShadow: "0 2px 10px rgba(0, 0, 0, 0.5)",
  },
  statLabel: {
    fontSize: "clamp(12px, 3vw, 16px)",
    fontWeight: "600",
    color: "rgba(255, 255, 255, 0.8)",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  actionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    width: "100%",
    maxWidth: "300px",
  },
  primaryButton: {
    padding: "16px 32px",
    fontSize: "clamp(16px, 4vw, 20px)",
    fontWeight: "700",
    color: "white",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    borderRadius: "16px",
    cursor: "pointer",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
    textTransform: "uppercase",
    letterSpacing: "1px",
    minHeight: "56px", // Large tap target for mobile
    transition: "all 0.2s ease",
  },
  secondaryButton: {
    padding: "14px 28px",
    fontSize: "clamp(14px, 3.5vw, 18px)",
    fontWeight: "600",
    color: "rgba(255, 255, 255, 0.9)",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "12px",
    cursor: "pointer",
    minHeight: "48px", // Large tap target for mobile
    transition: "all 0.2s ease",
  },
  skipHint: {
    position: "absolute",
    bottom: "20px",
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.6)",
    fontWeight: "500",
    pointerEvents: "none",
  },
};
